import { Option, RootNode } from "../types"
import { makeColorEnum, makeEnumString, makeStateEnum, makeStatusEnum, serializeDepedency } from "../utils"
import { DateTimeService } from "../../services/date-time-service"
import { getMembershipOptions, getSupplierOptions, getWorkspaceOptions } from "../../options/utils"
import { TreeGridTranslations } from "../hooks/use-translations"
import { makeGanttColumn } from "../base/layout/make-gantt-column"
import { CalendarSettingsViewModel } from "../../calendar-settings/api/calendar-settings"
import {
  ColumnName,
  ColumnSettings,
  getColumnSettings,
  getDefaultVisibleColumnNames,
  LeftColumnName,
} from "./column-settings"

type ColumnConfig = Partial<TCol> & {
  _id: ColumnName // internal id used for mapping columns,
  Value?: unknown
  header?: string
}
type ColumnMap = Record<ColumnName, ColumnConfig>

const makeLeftCols = (
  columnMap: ColumnMap,
  columnSettings: ColumnSettings,
  showGantt: boolean,
  enableTimeComponent: boolean
) => {
  const excluded = new Set<LeftColumnName>()

  if (!showGantt || (showGantt && enableTimeComponent)) {
    excluded.add("duration")
    excluded.add("daysLeft")
  }

  return columnSettings.leftColumnOrder.filter((col) => !excluded.has(col)).map((col) => columnMap[col])
}

const makeRightCols = (columnMap: ColumnMap, columnSettings: ColumnSettings) => {
  return columnSettings.rightColumnOrder.map((col) => columnMap[col])
}

export const makeColumns = ({
  root,
  showActualBar,
  showBaselineBar,
  defaultZoom,
  zoomList,
  options,
  translations,
  showGantt,
  visibleColumns = getDefaultVisibleColumnNames(showGantt, root.enableTimeComponent),
  calendarSettings,
}: MakeColumnsProps) => {
  const columnSettings = getColumnSettings(showGantt)
  const membershipOptions = getMembershipOptions(options)
  const membershipNames = membershipOptions.map(({ name }) => name)
  const membershipIds = membershipOptions.map(({ id }) => id)
  const membershipEnum = makeEnumString(membershipNames)
  const membershipEnumKeys = makeEnumString(membershipIds)

  const supplierOptions = getSupplierOptions(options)
  const supplierNames = supplierOptions.map(({ name }) => name)
  const supplierIds = supplierOptions.map(({ id }) => id)
  const supplierEnum = makeEnumString(supplierNames)
  const supplierEnumKeys = makeEnumString(supplierIds)

  const workspaceOptions = getWorkspaceOptions(options)
  const workspaceNames = workspaceOptions.map(({ name }) => name)
  const workspaceIds = workspaceOptions.map(({ id }) => id)
  const workspaceEnum = makeEnumString(workspaceNames)
  const workspaceEnumKeys = makeEnumString(workspaceIds)

  const statusEnum = makeStatusEnum(translations)
  const stateEnum = makeStateEnum(translations)

  const ganttBarColorEnum = makeColorEnum(translations)

  const ganttColumn = makeGanttColumn({
    ganttBase: root.ganttBaseDate,
    ganttFinish: root.ganttFinishDate,
    showActualBar,
    showBaselineBar,
    defaultZoom,
    zoomList,
    calendarSettings,
    enableTimeComponent: root.enableTimeComponent,
  })

  const dateTimeService = new DateTimeService({
    dateFormat: root.dateFormat,
    enableTimeComponent: root.enableTimeComponent,
  })
  const dateTimeFormat = dateTimeService.getFormat()

  const rowNumberColumn: ColumnConfig = {
    _id: "rowNumber",
    Name: "1",
    CanSort: 0,
    CanHide: 0,
    header: translations.rowNumberColumnHeader,
    Class: "rowNumber",
  }

  const openColumn: ColumnConfig = {
    _id: "open",
    Name: "open",
    Type: "Icon",
    Visible: visibleColumns.includes("open"),
    Width: 32,
    header: translations.openColumnHeader,
    CanExport: 0,
    CanPrint: 0,
    CanSort: 0,
    CanHide: 0,
    Class: "openButton",
  }

  const panelColumn: ColumnConfig = {
    _id: "Panel",
    Name: "Panel",
    header: translations.actionsColumnHeader,
    Width: 74,
    CanHide: 0,
  }

  const titleColumn: ColumnConfig = {
    _id: "title",
    Name: "title",
    MinWidth: 160,
    Width: 250,
    CaseSensitive: 0,
    header: translations.titleColumnHeader,
  }

  const taskNumberColumn: ColumnConfig = {
    _id: "taskNumber",
    Name: "taskNumber",
    MinWidth: 80,
    MaxWidth: 80,
    CaseSensitive: 0,
    header: translations.taskNumberColumnHeader,
  }

  const descriptionColumn: ColumnConfig = {
    _id: "description",
    Name: "description",
    Visible: visibleColumns.includes("description"),
    CaseSensitive: 0,
    Type: "Lines",
    MinWidth: 200,
    RelWidth: showGantt ? undefined : 1,
    AcceptEnters: 1,
    header: translations.descriptionColumnHeader,
    CanSort: 0,
  }

  const statusColumn: ColumnConfig = {
    _id: "status",
    Name: "status",
    Type: "Enum",
    Enum: statusEnum,
    MinWidth: 120,
    Visible: visibleColumns.includes("status"),
    header: translations.statusColumnHeader,
  }

  const stateColumn: ColumnConfig = {
    _id: "state",
    Name: "state",
    Type: "Enum",
    Enum: stateEnum,
    CanSort: 0,
    CanExport: 0,
    MinWidth: 64,
    MaxWidth: 64,
    Visible: visibleColumns.includes("state"),
    header: translations.stateColumnHeader,
  }

  const statusDescriptionColumn: ColumnConfig = {
    _id: "statusDescription",
    Name: "statusDescription",
    Visible: visibleColumns.includes("statusDescription"),
    CaseSensitive: 0,
    Type: "Lines",
    MinWidth: 200,
    Width: 280,
    AcceptEnters: 1,
    header: translations.statusDescriptionColumnHeader,
    CanSort: 0,
  }

  const managersColumn: ColumnConfig = {
    _id: "managers",
    Name: "managers",
    Type: "Enum",
    Enum: membershipEnum,
    EnumKeys: membershipEnumKeys,
    Value: 0,
    Range: true,
    MinWidth: 120,
    CanHide: root.belongsToUser ? 0 : 1, // user should never be able to see managers column in his personal task list
    Visible: !root.belongsToUser && visibleColumns.includes("managers"),
    header: translations.responsibleColumnHeader,
  }

  const participantsColumn: ColumnConfig = {
    _id: "participants",
    Name: "participants",
    Type: "Enum",
    Enum: membershipEnum,
    EnumKeys: membershipEnumKeys,
    Value: 0,
    Range: true,
    MinWidth: 120,
    CanHide: root.belongsToUser ? 0 : 1, // user should never be able to see participants column in his personal task list
    Visible: !root.belongsToUser && visibleColumns.includes("participants"),
    header: translations.participantsColumnHeader,
  }

  const durationColumn: ColumnConfig = {
    _id: "duration",
    Name: "duration",
    Type: "Int",
    Visible: visibleColumns.includes("duration"),
    Align: "Center",
    header: translations.durationColumnHeader,
  }

  const daysLeftColumn: ColumnConfig = {
    _id: "daysLeft",
    Name: "daysLeft",
    Type: "Int",
    Visible: visibleColumns.includes("daysLeft"),
    header: translations.daysLeftColumnHeader,
    Align: "Center",
  }

  const plannedStartDateColumn: ColumnConfig = {
    _id: "plannedStartDate",
    Name: "plannedStartDate",
    Type: "Date",
    Format: dateTimeFormat,
    Width: 154,
    Visible: visibleColumns.includes("plannedStartDate"),
    header: translations.plannedStartDateColumnHeader,
  }

  const plannedEndDateColumn: ColumnConfig = {
    _id: "plannedEndDate",
    Name: "plannedEndDate",
    Type: "Date",
    Format: dateTimeFormat,
    Width: 154,
    Visible: visibleColumns.includes("plannedEndDate"),
    header: translations.plannedEndDateColumnHeader,
  }

  const actualStartDateColumn: ColumnConfig = {
    _id: "actualStartDate",
    Name: "actualStartDate",
    Type: "Date",
    Format: dateTimeFormat,
    Width: 154,
    Visible: visibleColumns.includes("actualStartDate"),
    header: translations.actualStartDateColumnHeader,
  }

  const actualEndDateColumn: ColumnConfig = {
    _id: "actualEndDate",
    Name: "actualEndDate",
    Type: "Date",
    Format: dateTimeFormat,
    Visible: visibleColumns.includes("actualEndDate"),
    Width: 154,
    header: translations.actualEndDateColumnHeader,
  }

  const actualBarStartDateColumn: ColumnConfig = {
    _id: "actualBarStartDate",
    Name: "actualBarStartDate",
    Type: "Date",
    Visible: 0,
    CanHide: 0,
    CanExport: 0,
    CanPrint: 0,
    header: "HiddenActualBarStartDate",
  }

  const actualBarEndDateColumn: ColumnConfig = {
    _id: "actualBarEndDate",
    Name: "actualBarEndDate",
    Type: "Date",
    Visible: 0,
    CanHide: 0,
    CanExport: 0,
    CanPrint: 0,
    header: "HiddenActualBarEndDate",
  }

  const basePlanStartDateColumn: ColumnConfig = {
    _id: "basePlanStartDate",
    Name: "basePlanStartDate",
    Type: "Date",
    Visible: 0,
    CanHide: 0,
    header: translations.basePlanStartDateColumnHeader,
  }

  const basePlanEndDateColumn: ColumnConfig = {
    _id: "basePlanEndDate",
    Name: "basePlanEndDate",
    Type: "Date",
    Visible: 0,
    CanHide: 0,
    header: translations.basePlanEndDateColumnHeader,
  }

  const ganttAncestorsColumn: ColumnConfig = {
    _id: "ganttAncestors",
    Name: "ganttAncestors",
    Type: "Html",
    CanHide: 0,
    Visible: 0,
    CanPrint: 0,
    CanExport: 0,
  }

  // @ts-ignore
  window.FormatGanttDependencies = function (Grid: TGrid, Row: TRow) {
    if (!Grid || !Row) return
    // @ts-ignore
    const dependencies = JSON.parse(Row.dependencies || "[]")
    const formatted = dependencies
      .map((dependency: any) => {
        const dependencyRow = Grid.GetRowById(dependency.taskId)
        const rowIndex = Grid.GetRowIndex(dependencyRow)
        return { ...dependency, taskId: rowIndex }
      })
      .map(serializeDepedency)
      .join(",")
    return formatted
  }

  const dependenciesColumn: ColumnConfig = {
    _id: "dependencies",
    Name: "dependencies",
    CanEdit: 0,
    CanSort: 0,
    Visible: 0,
    CanHide: 0,
    CanExport: 0,
    CanPrint: 0,
  }

  const ganttDependenciesColumn: ColumnConfig = {
    _id: "ganttAncestors",
    Name: "ganttAncestors",
    Type: "Html",
    Visible: 0,
    CanPrint: 1,
    CanExport: 1,
    CanEdit: 0,
    CanHide: 1,
    header: translations.ganttDependenciesColumnHeader,
    Formula: "FormatGanttDependencies(Grid, Row)",
  }

  const suppliersColumn: ColumnConfig = {
    _id: "suppliers",
    Name: "suppliers",
    Type: "Enum",
    Enum: supplierEnum,
    EnumKeys: supplierEnumKeys,
    Value: 0,
    Range: true,
    MinWidth: 120,
    // the suppliers column should be included in the grid only if the grid belongs to an organisation, and the organisation has suppliers
    // with CanHide and Visible both set to 0, user will neither see the column nor be able to show it from column picker
    CanHide: !root.belongsToUser && supplierOptions.length ? 1 : 0,
    Visible: supplierOptions.length && visibleColumns.includes("suppliers"),
    header: translations.suppliersColumnHeader,
  }

  const workspacesColumn: ColumnConfig = {
    _id: "workspaces",
    Name: "workspaces",
    Type: "Enum",
    Enum: workspaceEnum,
    EnumKeys: workspaceEnumKeys,
    Value: 0,
    Range: true,
    MinWidth: 120,
    // the workspaces column should be included in the grid only if the grid belongs to an organisation, and the organisation has workspaces
    // with CanHide and Visible both set to 0, user will neither see the column nor be able to show it from column picker
    CanHide: !root.belongsToUser && workspaceOptions.length ? 1 : 0,
    Visible: workspaceOptions.length && visibleColumns.includes("workspaces"),
    header: translations.workspacesColumnHeader,
  }

  const orderColumn: ColumnConfig = {
    _id: "order",
    Name: "order",
    Type: "Int",
    Visible: 0,
    CanHide: 0,
    CanPrint: 0,
    CanExport: 0,
    CanSort: 0,
  }

  const canCreateTasksColumn: ColumnConfig = {
    _id: "canCreateTasks",
    Name: "canCreateTasks",
    Type: "Bool",
    Visible: 0,
    CanHide: 0,
    CanPrint: 0,
    CanExport: 0,
    CanSort: 0,
  }

  const ganttBarColorColumn: ColumnConfig = {
    _id: "ganttBarColor",
    Name: "ganttBarColor",
    Type: "Enum",
    CanFilter: 1,
    Enum: ganttBarColorEnum,
    MinWidth: 120,
    CanHide: 1,
    Visible: visibleColumns.includes("ganttBarColor"),
    header: translations.color,
  }

  const ganttGanttHtmlRightColumn: ColumnConfig = {
    _id: "ganttGanttHtmlRight",
    Name: "ganttGanttHtmlRight",
    Visible: 0,
    CanHide: 0,
    CanExport: 0,
    CanPrint: 0,
    header: "HiddenGanttGanttHtmlRight",
  }

  const linksAndAttachmentsColumn: ColumnConfig = {
    _id: "linksAndAttachments",
    Name: "linksAndAttachments",
    Type: "Html",
    Visible: visibleColumns.includes("linksAndAttachments"),
    header: translations.linksAndAttachmentsColumnHeader,
    MinWidth: 120,
  }

  const completionPercentageColumn: ColumnConfig = {
    _id: "completionPercentage",
    Name: "completionPercentage",
    Type: "Int",
    header: translations.completionColumnHeader,
    Visible: visibleColumns.includes("completionPercentage"),
    Width: 64,
  }

  const columnMap: ColumnMap = {
    rowNumber: rowNumberColumn,
    open: openColumn,
    Panel: panelColumn,
    taskNumber: taskNumberColumn,
    title: titleColumn,
    status: statusColumn,
    completionPercentage: completionPercentageColumn,
    state: stateColumn,
    statusDescription: statusDescriptionColumn,
    managers: managersColumn,
    participants: participantsColumn,
    suppliers: suppliersColumn,
    duration: durationColumn,
    plannedStartDate: plannedStartDateColumn,
    plannedEndDate: plannedEndDateColumn,
    daysLeft: daysLeftColumn,
    description: descriptionColumn,
    actualStartDate: actualStartDateColumn,
    actualEndDate: actualEndDateColumn,
    actualBarEndDate: actualBarEndDateColumn,
    actualBarStartDate: actualBarStartDateColumn,
    basePlanStartDate: basePlanStartDateColumn,
    basePlanEndDate: basePlanEndDateColumn,
    workspaces: workspacesColumn,
    ganttAncestors: ganttAncestorsColumn,
    ganttDependencies: ganttDependenciesColumn,
    ganttGanttHtmlRight: ganttGanttHtmlRightColumn,
    ganttBarColor: ganttBarColorColumn,
    linksAndAttachments: linksAndAttachmentsColumn,
    dependencies: dependenciesColumn,
    order: orderColumn,
    canCreateTasks: canCreateTasksColumn,
    gantt: ganttColumn,
  }

  return {
    LeftCols: makeLeftCols(columnMap, columnSettings, showGantt, root.enableTimeComponent),
    Cols: [],
    RightCols: makeRightCols(columnMap, columnSettings),
  }
}

type MakeColumnsProps = {
  root: RootNode
  showActualBar: boolean
  showBaselineBar: boolean
  showGantt: boolean
  translations: TreeGridTranslations
  defaultZoom: string
  visibleColumns?: string[]
  zoomList: any[]
  options: Option[]
  calendarSettings: CalendarSettingsViewModel
}
